import React, {useState} from 'react';
import {StatesButton, StatesButtonStates, ThreeDotsLoader, ButtonSize} from 'wix-ui-tpa/cssVars';
import {classes} from './PayNowButton.st.css';
import {usePanorama, useFedopsLogger} from '@wix/yoshi-flow-editor';
import {usePaymentsApi} from '../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {useControllerProps} from '../../Widget/ControllerContext';
import {toMonitorError} from '../../../../domain/utils/errorMonitor.utils';
import {useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {FedopsInteractions} from '../../constants';
import {PaymentFlowMonitorBreadcrumbs, PaymentResult} from '../../../../types/paymentRequestApp.types';
import {AddPaymentInfo} from '../../../../domain/utils/analytics.utils';

export const PayNowButtonDataHook = 'pay-now-button';

export const PayNowButton = () => {
  const localeKeys = useLocaleKeys();
  const [payNowButtonState, setPayNowButtonState] = useState(StatesButtonStates.IDLE);
  const [payNowButtonDisabled, setPayNowButtonDisabled] = useState(false);
  const {
    paymentRequestStore: {paymentRequest, sendClickOnPayNowButtonBIEvent},
    navigationStore: {navigateToThankYouPage, trackEvent, navigateToLinkExpiredPage},
  } = useControllerProps();
  const panorama = usePanorama();
  const fedops = useFedopsLogger();
  const {paymentsApi, activePaymentId} = usePaymentsApi();

  const isLinkStillValid = (): boolean => {
    const now = new Date().getTime();
    const expirationDateTimeStamp = paymentRequest?.expirationDateTimeStamp ?? now;
    if (expirationDateTimeStamp < now) {
      navigateToLinkExpiredPage();
      return false;
    }

    return true;
  };

  const validateAndSubmit = async () => {
    try {
      setPayNowButtonState(StatesButtonStates.IN_PROGRESS);
      setPayNowButtonDisabled(true);
      sendClickOnPayNowButtonBIEvent(activePaymentId);
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).start();
      fedops.interactionStarted(FedopsInteractions.ValidatePaymentInteraction);
      const isStillValid = isLinkStillValid();
      const validation = await paymentsApi?.validate();
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).finish();
      fedops.interactionEnded(FedopsInteractions.ValidatePaymentInteraction);

      if (!validation?.isValid || !isStillValid) {
        return;
      }

      panorama.errorMonitor().addBreadcrumb({
        message: PaymentFlowMonitorBreadcrumbs.validation,
      });
      await paymentsApi?.prepareToPay?.();
      trackEvent(...AddPaymentInfo({activePaymentMethod: activePaymentId}));

      panorama.transaction(FedopsInteractions.Pay).start();
      fedops.interactionStarted(FedopsInteractions.Pay);
      const paymentResult = await paymentsApi?.pay?.({orderId: paymentRequest.paymentGatewayOrderId});
      panorama.transaction(FedopsInteractions.Pay).finish();
      fedops.interactionEnded(FedopsInteractions.Pay);

      panorama.errorMonitor().addBreadcrumb({message: PaymentFlowMonitorBreadcrumbs.paid});

      /* istanbul ignore else */
      if (
        paymentResult?.clientStatus === PaymentResult.Approved ||
        /* istanbul ignore next */
        paymentResult?.clientStatus === PaymentResult.Pending
      ) {
        navigateToThankYouPage();
      }
    } catch (e) {
      /* istanbul ignore next */
      panorama.errorMonitor().reportError(...toMonitorError(e, PaymentFlowMonitorBreadcrumbs.error));
      /* istanbul ignore next */
      await paymentsApi?.cancelPayment?.();
    } finally {
      setPayNowButtonState(StatesButtonStates.IDLE);
      setPayNowButtonDisabled(false);
    }
  };

  return (
    <StatesButton
      className={classes.payNowButton}
      data-hook={PayNowButtonDataHook}
      disabled={payNowButtonDisabled}
      upgrade={true}
      size={ButtonSize.medium}
      onClick={() => {
        void validateAndSubmit();
      }}
      idleContent={localeKeys.checkout.payLinks.mainCta()}
      state={payNowButtonState}
      inProgressContent={<ThreeDotsLoader className={classes.threeDotButton} />}
      wrapContent
    />
  );
};
